import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FolderPlus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';

interface Props {
  open: boolean;
  onClose: () => void;
  parentFolderId?: string;
  parentFolderName?: string;
}

export function NewFolderModal({ open, onClose, parentFolderId, parentFolderName }: Props) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const utils = trpc.useUtils();

  const createMutation = trpc.skills.createFolder.useMutation({
    onSuccess: () => {
      utils.skills.getFolders.invalidate();
      toast.success(t('skills.folders.created_toast'));
      setName('');
      onClose();
    },
    onError: (err) => toast.error(`Error: ${err.message}`),
  });

  const handleSubmit = () => {
    if (name.trim()) createMutation.mutate({ name: name.trim(), parentFolderId });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setName(''); onClose(); } }}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderPlus className="h-4 w-4" />
            {parentFolderName ? t('skills.folders.new_in_title', { name: parentFolderName }) : t('skills.folders.new_title')}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-1">
          <Input
            autoFocus
            placeholder={t('skills.folders.name_placeholder')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
            maxLength={50}
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => { setName(''); onClose(); }}>
              {t('common.ui.cancel')}
            </Button>
            <Button onClick={handleSubmit} disabled={!name.trim() || createMutation.isPending}>
              {t('skills.folders.create_btn')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
